import { getCustomerById, getCustomerLedgerTransactions, getAllInvoiceDetails, useMessagesLookup } from "@api";
import { updateCustomer, useMerchant } from "@auth";
import { useAiFeatures } from "@flags";
import {
    Button,
    Drawer,
    DrawerBody,
    DrawerFooter,
    GridSection,
    GridSectionBody,
    GridSectionHeader,
    GridSectionItem,
    GridSectionSpan,
    InvoiceDetailsModal,
    InvoiceGroup,
    MessageContentModal,
    Timeline
} from "@components";
import NoteSourceBadge from "@components/NoteSourceBadge";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import { formatAmount } from "@utils";
import { formatDateTime } from "@utils/Formatters";
import { useEffect, useState, useMemo, forwardRef, useImperativeHandle } from "react";
import { useQuery } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { CheckLg } from "react-bootstrap-icons";
import { PaperAirplaneIcon, BellIcon } from "@heroicons/react/24/outline";
import AiSparkleIcon from "../../../portal-template/components/SmartNoteModal/AiSparkleIcon";
import { groupTransactions } from "./utils/groupTransactions";

const TradeAccountDetails = forwardRef(({ customerId, onClose, onChange, openSingleRequest }, ref) => {
  const { hasPermission, merchant } = useMerchant();
  const { openSmartNote } = useOutletContext() ?? {};
  const smartNoteEnabled = useAiFeatures();
  const { getTemplateNameById } = usePaymentRequestTemplatesContext();
  const [customer, setCustomer] = useState(null);
  const [activity, setActivity] = useState([]);
  const [invoiceGroups, setInvoiceGroups] = useState(null);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [isInvoiceDetailsModalOpen, setIsInvoiceDetailsModalOpen] = useState(false);
  const [viewMessage, setViewMessage] = useState(null);

  const invoiceNumbers = useMemo(() =>
    (invoiceGroups ?? []).flatMap(g => g.invoices).map(inv => inv.transactionNumber).filter(Boolean),
    [invoiceGroups]);

  const { data: invoiceDetailsArray } = useQuery({
    queryKey: ["allInvoiceDetails", customerId, invoiceNumbers],
    queryFn: () => getAllInvoiceDetails(customerId, invoiceNumbers),
    enabled: !!customerId && invoiceNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    gcTime: 10 * 60 * 1000,
  });

  const invoiceDetailsMap = useMemo(() => {
    if (!invoiceDetailsArray) return {};
    return Object.fromEntries(invoiceDetailsArray.map(d => [d.number, d]));
  }, [invoiceDetailsArray]);

  const requestIds = useMemo(() =>
    (customer?.paymentRequests ?? []).map(x => x.id).filter(Boolean),
    [customer]);

  const { data: requestMessages } = useMessagesLookup(requestIds);

  const archivedRequestIds = useMemo(() =>
    new Set((requestMessages ?? []).filter(m => m.hasArchive).map(m => m.paymentRequestId)),
    [requestMessages]);

  useEffect(() => {
    async function fetchData() {
      const data = await getCustomerById(customerId);
      setCustomer(data);
      if (data?.accountCode) {
        const transactions = await getCustomerLedgerTransactions(data.accountCode);
        setInvoiceGroups(groupTransactions(transactions));
      }
    }

    if (customerId) {
      fetchData();
    }

  }, [customerId]);

  useEffect(() => {
    if (customer) {
      mapTimeline(customer);
    }
  }, [customer, archivedRequestIds]);

  const reloadData = async () => {
    const data = await getCustomerById(customerId);
    setCustomer(data);
    if (data?.accountCode) {
      const transactions = await getCustomerLedgerTransactions(data.accountCode);
      setInvoiceGroups(groupTransactions(transactions));
    }
  }

  useImperativeHandle(ref, () => ({
    reloadData
  }));

  const mapTimeline = (customerData) => {
    let events = [];

    let notes = customerData.notes.map(x => ({
      title: "Note added",
      // Source badge distinguishes notes typed in Ontik from ones synced from the ERP.
      subTitle: (
        <span className="inline-flex items-center gap-1.5">
          <NoteSourceBadge source={x.source} />
          <span>{`${formatDateTime(x.createdOn)} - ${x.createdBy}`}</span>
        </span>
      ),
      content: x.content,
      taggedUsers: x.taggedUsers,
      date: x.createdOn,
      nodeContent: {
        text: x.createdBy?.split(" ").map((n) => n[0]).join("") ?? "",
      }
    }));

    events = events.concat(notes);

    let paymentEvents = customerData.payments.filter(x => x.status === "PaymentSucceeded").map(x => {
      return {
        date: x.initialisedOn,
        title: (<div className="text-green-700">Payment received</div>),
        amount: (<div className="text-green-700">{formatAmount(x.amount)}</div>),
        subTitle: formatDateTime(x.initialisedOn),
        nodeContent: {
          icon: CheckLg,
          color: "success"
        }
      }
    });

    events = events.concat(paymentEvents);

    const getActionText = (x) => {
      if (x.invoicesRequested) {
        if (x.invoicesRequested === "All") return "All invoices";
        if (x.invoicesRequested === "OverdueAndDueThisMonth") return "Overdue and due";
        if (x.invoicesRequested === "None") return "No invoices requested";
        return "Overdue";
      }
      return ``;
    };

    let requestEvents = customerData.paymentRequests.map(x => {
      const templateName = x.messageTemplateName ||
        (x.paymentRequestTemplateId ? getTemplateNameById(x.paymentRequestTemplateId) : null);

      return {
        title: `${templateName ?? 'Payment Request'} sent`,
        subTitle: `${formatDateTime(x.requestedOn)} - ${getActionText(x)} `,
        amount: formatAmount(x.amountRequested),
        user: "Alfie Walliss",
        date: x.requestedOn,
        nodeContent: {
          icon: PaperAirplaneIcon
        },
        onView: archivedRequestIds.has(x.id)
          ? () => setViewMessage({ paymentRequestId: x.id, date: x.requestedOn })
          : undefined
      }
    });

    events = events.concat(requestEvents);

    let reminderEvents = customerData.paymentRequests.flatMap(x =>
      (x.reminders ?? []).map(r => ({
        title: `Reminder sent`,
        subTitle: `${formatDateTime(r.sentOn)} - ${getActionText(x)}`,
        amount: formatAmount(x.amountRequested),
        date: r.sentOn,
        nodeContent: {
          icon: BellIcon
        },
        onView: archivedRequestIds.has(x.id)
          ? () => setViewMessage({ paymentRequestId: x.id, date: r.sentOn })
          : undefined
      }))
    );

    events = events.concat(reminderEvents);

    setActivity(events);
  }

  const hasInvoices = invoiceGroups?.length > 0;

  return (
    <>
    <Drawer
      title="Trade account details"
      isOpen={!!customerId}
      toggle={onClose}>
      {customer && <>
        <DrawerBody>
          <GridSection>
            <GridSectionSpan>
              <h2 className="text-3xl font-medium m-0 mr-2">{customer.name}</h2>
            </GridSectionSpan>
            <GridSectionItem>
              <GridSectionHeader>Contact email</GridSectionHeader>
              <GridSectionBody>{customer.email}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Contact number</GridSectionHeader>
              <GridSectionBody>{customer.phone}</GridSectionBody>
            </GridSectionItem>
          </GridSection>
          <GridSection>
            <GridSectionItem>
              <GridSectionHeader>Account balance</GridSectionHeader>
              <GridSectionBody>{formatAmount(customer.balance)}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Overdue</GridSectionHeader>
              <GridSectionBody>{formatAmount(customer.overdue)}</GridSectionBody>
            </GridSectionItem>
          </GridSection>
          <GridSection>
            <GridSectionItem>
              <GridSectionHeader>Credit used</GridSectionHeader>
              <GridSectionBody>{formatAmount(customer.balance)}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Credit limit</GridSectionHeader>
              <GridSectionBody>{formatAmount(customer.creditLimit)}</GridSectionBody>
            </GridSectionItem>
            {customer.creditBureauLimit != null && <GridSectionItem>
              <GridSectionHeader>Credit bureau limit</GridSectionHeader>
              <GridSectionBody>{formatAmount(customer.creditBureauLimit)}</GridSectionBody>
            </GridSectionItem>}
          </GridSection>

          {hasInvoices && (
            <div className="px-5 py-5 border-b">
              <p className="font-semibold m-0 mb-4">Invoices</p>
              <div className="flex flex-col gap-2">
                {invoiceGroups.map(group => (
                  <InvoiceGroup
                    key={group.name}
                    name={group.name}
                    invoices={group.invoices}
                    showStatus={false}
                    invoiceDetailsMap={invoiceDetailsMap}
                    onOpenModal={(d) => { setSelectedInvoiceDetails(d); setIsInvoiceDetailsModalOpen(true); }}
                  />
                ))}
              </div>
            </div>
          )}

          <Timeline activity={activity} customerId={customerId} onNoteAdded={onChange} />
        </DrawerBody>
        {(hasPermission(updateCustomer) || (smartNoteEnabled && openSmartNote)) && <DrawerFooter>
          {smartNoteEnabled && openSmartNote && (
            <Button
              color="secondary"
              className="flex-1"
              onClick={() => {
                openSmartNote({ id: customerId, name: customer.name, accountCode: customer.accountCode });
                onClose();
              }}>
              <AiSparkleIcon className="shrink-0 mr-2 size-4" />
              Intelligent Analysis
            </Button>
          )}
          {hasPermission(updateCustomer) && (
            <Button
              color="primary"
              className={smartNoteEnabled && openSmartNote ? "flex-1" : "ml-auto"}
              onClick={openSingleRequest}>
              <PaperAirplaneIcon aria-hidden="true" className="shrink-0 inline mr-2 size-4" />
              Request payment
            </Button>
          )}
        </DrawerFooter>}
      </>}
    </Drawer>

    <InvoiceDetailsModal
      invoice={selectedInvoiceDetails}
      isOpen={isInvoiceDetailsModalOpen}
      onClose={() => setIsInvoiceDetailsModalOpen(false)}
      orgId={merchant?.organisationId}
    />

    <MessageContentModal
      isOpen={!!viewMessage}
      onClose={() => setViewMessage(null)}
      paymentRequestId={viewMessage?.paymentRequestId}
      targetDate={viewMessage?.date}
    />
    </>
  );
});

export default TradeAccountDetails;
